function calculateBearing(point1, point2) {
  const toRadians = (degrees) => degrees * (Math.PI / 180);
  const toDegrees = (radians) => radians * (180 / Math.PI);

  const latitude1 = toRadians(point1[1]);
  const latitude2 = toRadians(point2[1]);
  const deltaLongitude = toRadians(point2[0] - point1[0]);

  const y = Math.sin(deltaLongitude) * Math.cos(latitude2);
  const x = Math.cos(latitude1) * Math.sin(latitude2) - Math.sin(latitude1) * Math.cos(latitude2) * Math.cos(deltaLongitude);
  const theta = Math.atan2(y, x);

  return (toDegrees(theta) + 180) % 360; // Normalize to 0-360 degrees
}

function createElementWithAttributes(tag, attributes) {
  const element = document.createElement(tag);
  for (const key in attributes) {
    if (attributes.hasOwnProperty(key)) {
      element.setAttribute(key, attributes[key]);
    }
  }
  return element;
}

/**
 * Class to represent a vehicle with a ghost vehicle
 * @class
 * @property {Array} path - The route data for the vehicle.
 * @property {Object} vehicle - The vehicle object.
 * @property {Object} ghost - The ghost vehicle object.
 * @property {Array} vehicles - The vehicle and ghost vehicle objects.
 * @property {string} vehicles[].type - The type of the vehicle.
 * @property {number} vehicles[].index - The path index/location of the vehicle.
 * @property {Object} vehicles[].element - The element for the vehicle.
 * @property {Object} vehicles[].marker - The marker for the
 */
class VehicleWithGhost {
  /**
   * Creates an instance of VehicleWithGhost.
   * @param {Object} vehicle - The vehicle data.
   * @param {string} vehicle.id - The ID of the vehicle.
   * @param {string} vehicle.source - The source of the vehicle.
   * @param {Array} route - The route data for the vehicle.
   */
  constructor(vehicle, route) {
    const vehicleElement = createElementWithAttributes('map-marker', {
      type: 'truck',
      regnumber: vehicle.id,
      progress: 56,
      status: vehicle.status ?? 'active',
      route: vehicle.source,
      soc: 100,
      distance: 0,
      rdt: 0,
      angle: 0,
    });

    const ghostElement = createElementWithAttributes('map-marker', {
      type: 'truck-ghost',
      regnumber: vehicle.id,
      progress: 56,
      status: 'ghost',
      route: vehicle.source,
      soc: 100,
      distance: 0,
      rdt: 0,
      angle: 0,
    });

    // this.data = vehicle;
    this.path = route;
    this.vehicles = [
      {
        type: 'truck',
        index: Math.round((vehicle.progress * this.path.length) / 100),
        marker: null,
        element: vehicleElement,
      },
    ];

    if (vehicle.ghost > 0) {
      this.vehicles.push({
        type: 'truck-ghost',
        index: Math.round(((vehicle.progress + vehicle.ghost) * this.path.length) / 100),
        marker: null,
        element: ghostElement,
      });
    }
  }

  update(map) {
    for (let item of this.vehicles) {
      if (!item.marker) {
        item.marker = new mapboxgl.Marker(item.element).setLngLat(this.path[item.index]).addTo(map);
      }

      if (item.element.getAttribute('status') != 'charging') {
        item.index = (item.index + 1) % this.path.length;
        item.marker.setLngLat(this.path[item.index]);
      }

      // Update vehicle attributes
      item.element.setAttribute('distance', 80);
      item.element.setAttribute('rdt', (item.index / this.path.length) * 100);
      item.element.setAttribute('angle', calculateBearing(this.path[item.index], this.path[(item.index + 1) % this.path.length]));
      item.element.setAttribute('soc', 100 - (item.index / this.path.length) * 100);
      item.element.setAttribute('progress', (item.index / this.path.length) * 100);
    }
  }

  animate(progress = 0) {
    for (let item of this.vehicles) {
      if (!item.marker) {
        continue;
      }
      if (item.element.getAttribute('status') == 'charging') {
        continue;
      }

      const current = this.path[item.index];
      const next = this.path[(item.index + 1) % this.path.length];

      // Interpolate between current and next point
      const lng = current[0] + (next[0] - current[0]) * progress;
      const lat = current[1] + (next[1] - current[1]) * progress;
      item.marker.setLngLat([lng, lat]);
    }
  }
}
export default class DemoGhostVehicles {
  constructor(parent) {
    this.animating = false;
    this.parent = parent; // Reference to the map web component
    this.map = parent.map;
    this.ghosts = [];
  }

  load(vehicles, routes) {
    // Join route legs on source prefix to get full path
    const combined_routes = new Map();
    for (let source in routes) {
      const prefix = source.split('_')[0];
      if (combined_routes.get(prefix)) {
        combined_routes.set(prefix, combined_routes.get(prefix).concat(routes[source].points));
      } else {
        combined_routes.set(prefix, routes[source].points);
      }
    }

    for (let vehicle of vehicles) {
      let route = combined_routes.get(vehicle.source);
      if (route) {
        this.ghosts.push(new VehicleWithGhost(vehicle, route));
      }
    }
  }

  update() {
    for (let ghost of this.ghosts) {
      ghost.update(this.map);
    }
  }

  clear() {
    for (let ghost of this.ghosts) {
      for (let vehicle of ghost.vehicles) {
        if (vehicle.marker) {
          vehicle.marker.remove();
        }
      }
    }
    this.ghosts = [];
  }

  // Send no arguments to toggle animation
  animate(timestamp = 0) {
    if (!timestamp) {
      this.animating = !this.animating;
      this.lastUpdateTime = performance.now();
      this.update();
    }

    if (this.animating) {
      const delay = 250;
      const now = performance.now();
      const elapsed = now - this.lastUpdateTime;

      if (elapsed >= delay) {
        this.update();
        this.lastUpdateTime = now;
      }

      const progress = (elapsed % delay) / delay;
      for (let ghost of this.ghosts) {
        ghost.animate(progress);
      }

      requestAnimationFrame(this.animate.bind(this));
    }
  }
}
